import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiInputTextbox } from "@miplatform/components";
import { BUTTON_COLORS } from "@miplatform/constants";
import { ICategoryDropsResponse } from "@miplatform/types";
import { isRecentlyAdded } from "@miplatform/utils";
import { mdiTrashCanOutline } from "@mdi/js";
import { AccordionDetails, Box, Typography } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";
import {
  DROP_NAME_COL_WIDTH,
  PRICE_LVL_COL_WIDTH,
} from "../constants/constants";
import { DeleteCategoryDropModal } from "./DeleteCategoryDropModal";

export function CategoryDropRows({
  readOnly = false,
  form,
  transform,
  categoryId,
  categoryDrop,
  categoryDrops = [],
  priceLevels,
  activePriceLevelIndex,
  handleDeleteCategoryDrop,
}: CategoryDropRowsProps) {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedDrop, setSelectedDrop] =
    useState<ICategoryDropsResponse | null>(null);
  const [, setTimer] = useState(0);

  const { setValue } = form ?? {};
  const { classes, cx } = useStyles({ transform });

  // force re-render every 5 second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const getDropInfo = (
    priceLevel: IPriceLevelPayload,
    drop: { dropTypeId: string; categoryId: string }
  ) => {
    const dropPrices = priceLevel.dropPrices.find(
      (dp) =>
        dp.dropTypeId === drop.dropTypeId && dp.categoryId === drop.categoryId
    );
    const dropIndex = categoryDrops.findIndex(
      (cd) =>
        cd.dropTypeId === drop.dropTypeId && cd.categoryId === drop.categoryId
    );
    return {
      price: dropPrices?.price,
      modifiedOn: dropPrices?.modifiedOn,
      dropIndex,
    };
  };

  return (
    <AccordionDetails className={classes.accordionDetails}>
      {categoryDrop.map((drop) => {
        return (
          <Box key={drop.id} className={classes.dropBox}>
            <Box className={classes.dropNameCol}>{drop.dropType}</Box>
            <Box className={classes.scrollWrapper}>
              <Box className={classes.scrollContent}>
                {priceLevels.map((level, plIdx) => {
                  const { price, modifiedOn, dropIndex } = getDropInfo(
                    level,
                    drop
                  );
                  const isRecentlyModified = isRecentlyAdded(
                    modifiedOn,
                    5,
                    "seconds"
                  );
                  const isActive = activePriceLevelIndex === plIdx;

                  return (
                    <Box
                      key={level.id}
                      className={cx(
                        classes.priceLevelCol,
                        isActive
                          ? classes.priceLevelColActive
                          : isRecentlyModified && classes.recentlyModified
                      )}
                    >
                      <NumericFormat
                        key={activePriceLevelIndex}
                        customInput={MiInputTextbox}
                        value={price}
                        textfieldProps={{
                          startAdornment: (
                            <Typography
                              className={cx(
                                classes.priceText,
                                !level.isActive && classes.disabledText
                              )}
                            >
                              $
                            </Typography>
                          ),
                        }}
                        readOnly={readOnly || !isActive}
                        className={cx(
                          classes.priceInputField,
                          !level.isActive && classes.disabledInput
                        )}
                        decimalScale={2}
                        fixedDecimalScale
                        onValueChange={({ floatValue }) => {
                          setValue?.(
                            `dropPrices.${dropIndex}.price`,
                            floatValue || 0,
                            { shouldDirty: true }
                          );
                          setValue?.(
                            `dropPrices.${dropIndex}.dirty`, // for client only, to only send changed drop price to backend
                            true
                          );
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
            {!readOnly && (
              <MiButton
                disabled={activePriceLevelIndex !== null}
                startIcon={{ path: mdiTrashCanOutline }}
                color={BUTTON_COLORS.RED}
                size="small"
                onClick={() => {
                  setSelectedDrop(drop);
                  setOpenDeleteModal(true);
                }}
              />
            )}
          </Box>
        );
      })}
      <DeleteCategoryDropModal
        open={openDeleteModal}
        selectedDrop={selectedDrop}
        handleDeleteCategoryDrop={handleDeleteCategoryDrop}
        categoryId={categoryId}
        closeModal={() => {
          setOpenDeleteModal(false);
          setSelectedDrop(null);
        }}
      />
    </AccordionDetails>
  );
}

const useStyles = makeStyles<StyleProps>()((theme, { transform }) => ({
  accordionDetails: {
    backgroundColor: theme.palette.grey.light,
  },
  dropBox: {
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    marginTop: theme.spacing(1),
    height: 39,
  },
  priceInputField: {
    "& .MuiInputBase-input.MuiOutlinedInput-input": {
      padding: 0,
      fontSize: theme.fontSize.medium,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& input": {
      textAlign: "right",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: 0,
    },
  },
  disabledInput: {
    "& input": {
      color: theme.palette.grey.darkAccent,
    },
  },
  priceText: {
    fontSize: theme.fontSize.medium,
  },
  disabledText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.grey.darkAccent,
  },
  // shared styles with PriceLevelHeaderRow
  dropNameCol: {
    width: DROP_NAME_COL_WIDTH,
    minWidth: DROP_NAME_COL_WIDTH,
    fontWeight: theme.fontWeight.semiBold,
    color: theme.palette.primary.main,
    paddingLeft: theme.spacing(2),
  },
  scrollWrapper: {
    flex: 1,
    overflow: "hidden",
    minWidth: 0,
  },
  scrollContent: {
    display: "flex",
    transition: "transform 0.3s ease",
    transform,
  },
  priceLevelCol: {
    width: PRICE_LVL_COL_WIDTH,
    minWidth: PRICE_LVL_COL_WIDTH,
    flexShrink: 0,
    backgroundColor: theme.palette.grey.mediumLight,
    display: "flex",
    alignItems: "center",
    padding: 8,
    borderRight: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between",
  },
  priceLevelColActive: {
    backgroundColor: `${theme.palette.primary.light}`,
  },
  recentlyModified: {
    backgroundColor: theme.palette.orange.light,
  },
}));

interface CategoryDropRowsProps {
  readOnly?: boolean;
  form?: UseFormReturn<IPriceLevelPayload>;
  transform: string;
  categoryId: string;
  categoryDrop: ICategoryDropsResponse[];
  categoryDrops?: ICategoryDropsResponse[];
  priceLevels: IPriceLevelPayload[];
  activePriceLevelIndex: number | null;
  handleDeleteCategoryDrop: (
    categoryId: string,
    drop: Partial<ICategoryDropsResponse>
  ) => void;
}

interface StyleProps {
  transform: string;
}
